import { oklabToRgba, parseColorToRgba, rgbaToOklab } from "../utils/color-parser";

type BackgroundInfo = {
    id: number;
    percent: number;
    currentBackgroundColor: string;
    callback?: () => void;
};

export class BackgroundRegistry {
    private allBackground: Map<HTMLElement, BackgroundInfo> = new Map();
    private nextId: number = 0;

    private mixedColor: string = "inherit";
    private observer: IntersectionObserver;

    constructor() {
        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const background = this.allBackground.get(entry.target as HTMLElement);
                    if (!background) return;
                    background.percent = entry.intersectionRatio * 100;
                    background.currentBackgroundColor = getComputedStyle(entry.target as HTMLElement).backgroundColor;
                });
                this.mixedColor = this.getBestBackgroundColor(Array.from(this.allBackground.values()));
                this.allBackground.forEach(({ callback }) => callback?.());
            },
            {
                threshold: Array.from({ length: 1001 }, (_, i) => i / 1000),
                rootMargin: "-15% 0px"
            }
        );
    }

    private getBestBackgroundColor(results: BackgroundInfo[]) {
        const sumIntersections = results.reduce((sum, r) => sum + r.percent, 0) || 1;
        const weightedColors = results.map(r => {
            const weight = r.percent / sumIntersections;
            const color = rgbaToOklab(parseColorToRgba(r.currentBackgroundColor) || [0, 0, 0, 0]);
            if (!color) return [0, 0, 0, 0] as [number, number, number, number];
            return [color[0] * weight, color[1] * weight, color[2] * weight, color[3] * weight] as [number, number, number, number];
        });
        const avgColor = weightedColors.reduce<[number, number, number, number]>(
            (acc, curr) => [acc[0] + curr[0], acc[1] + curr[1], acc[2] + curr[2], acc[3] + curr[3]],
            [0, 0, 0, 0]
        );
        const rgbColor = oklabToRgba(avgColor);
        rgbColor[3] = 1;
        return `rgba(${Math.round(rgbColor[0])}, ${Math.round(rgbColor[1])}, ${Math.round(rgbColor[2])}, ${Number(rgbColor[3].toFixed(3))})`;
    }

    addBackground(background?: HTMLElement, callback?: () => void) {
        if (!background) return;
        const uid = this.nextId++;
        this.allBackground.set(background, {
            id: uid,
            percent: 100,
            currentBackgroundColor: getComputedStyle(background).backgroundColor,
            callback
        });
        this.observer.observe(background);
        return () => {
            this.observer.unobserve(background);
            this.allBackground.delete(background);
        };
    }

    getMixedColor() {
        return this.mixedColor;
    }

};
